import { Box, ButtonBase, Typography, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import "./ProjectDetailsPage.css";
import MapComponent from "../../Components/MapComponent/MapComponent";
import ProjectStatusPanelComponent from "../../Components/VerticalLegendComponent/VerticalLegendComponent";
import MiddleDropdown from "../../Components/MiddleDropdown/MiddleDropdown";
import { CalendarMonth, KeyboardArrowLeft, InfoOutlined } from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";
import StagesProgressComponent from "../../Components/StagesProgressComponent/StagesProgressComponent";
import { Link, useLocation, useOutletContext, useParams } from "react-router-dom";
import { formatMonthYear } from "../../Utils/date";
import QuarterlyReportsModal from "../../Components/QuarterlyReportsComponent/QuarterlyReportsModal";

interface Props {}

const ProjectDetailsPage = (props: Props) => {
  const { id, slug } = useParams();
  const { hideDelays, hideLegend, stages, project } = useOutletContext<any>();

  const [selectedStageIndex, setSelectedStageIndex] = useState(0);
  const [showQuarterlyModal, setShowQuarterlyModal] = useState(false);

  const { setBreadcrumbs, setBackButton } = useOutletContext<any>();
  const [selectedDropdown, setSelectedDropdown] = useState<string | null>("desc");

  const { state } = useLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const focusRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setBreadcrumbs([{ name: 'לו"ז המיזם', path: "#" }]);
    setBackButton({ title: "אודות המיזם", route: `/project-about/${id}` });

    if (state && state.selectedStageIndex) setSelectedStageIndex(state.selectedStageIndex);

    const resetFocus = document.getElementById("reset-focus");
    if (resetFocus) resetFocus.focus();
  }, [id, setBreadcrumbs, setBackButton]);

  const onChangeStage = (index: number) => {
    setSelectedStageIndex(index);
  };

  const onCloseQuarterlyModal = () => {
    setShowQuarterlyModal(false);
    focusRef.current?.focus();
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {(!hideLegend || !isMobile) && (
          <Grid size={{ xs: 12, md: 2 }}>
            <ProjectStatusPanelComponent />
          </Grid>
        )}
        <Grid size={{ xs: 12, md: 8 }}>
          <Box sx={{ pr: "30px", pl: "30px" }}>
            <StagesProgressComponent stages={stages} selectedIndex={selectedStageIndex} hideDelays={hideDelays} onChangeStage={onChangeStage} />
          </Box>

          <Grid container spacing={2} mb={10} sx={{ bgcolor: "#F3F7FB", borderRadius: 2, p: 3 }}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ bgcolor: "#FFFFFF", borderRadius: 2, p: 3 }} dir="rtl">
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "right", mb: 2 }}>
                  <CalendarMonth sx={{ fontSize: 24, color: "#0C3768", ml: 1 }} />
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0C3768" }}>תאריכי התחלה וסיום שלב</Typography>
                </Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#2196F3", textAlign: "right" }}>
                  תאריך סיום עדכני: {formatMonthYear(stages[selectedStageIndex].updated_end_date)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#2196F3",
                    textAlign: "right",
                    mb: 1,
                  }}
                >
                  תאריך התחלה עדכני: {formatMonthYear(stages[selectedStageIndex].updated_start_date)}
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0C3768", textAlign: "right" }}>
                  תאריך סיום מקורי: {formatMonthYear(stages[selectedStageIndex].original_end_date)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#0C3768",
                    textAlign: "right",
                    mb: 2,
                  }}
                >
                  תאריך התחלה מקורי: {formatMonthYear(stages[selectedStageIndex].original_start_date)}
                </Typography>
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, md: 3 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                overflowY: "auto",
                gap: "6px",
              }}
            >
              <MiddleDropdown
                title="תיאור השלב"
                body={stages[selectedStageIndex].description}
                icon="/images/exclamation_mark.png"
                isExpanded={selectedDropdown === "desc"}
                onClick={() => setSelectedDropdown(selectedDropdown === "desc" ? null : "desc")}
                customColor={"#FFFFFF"}
              />
              <MiddleDropdown
                title="גוף מבצע"
                body={stages[selectedStageIndex].company}
                isObject
                icon="/images/tools_logo.png"
                customColor={"#FFFFFF"}
                isExpanded={selectedDropdown === "company"}
                onClick={() => setSelectedDropdown(selectedDropdown === "company" ? null : "company")}
              />
              {/*<MiddleDropdown*/}
              {/*    title="שינויים מרבעונים קודמים"*/}
              {/*    body={[*/}
              {/*        ["שם של איש קשר", "mailname@mail.co.il"],*/}
              {/*        ["שם של איש קשר שעלול להיות ארוך", "mailname@mail.co.il"],*/}
              {/*        ["שם של איש קשר", "mailname@mail.co.il"],*/}
              {/*        ["שם של איש קשר ותפקיד אולי גם", "0542318611"],*/}
              {/*    ]}*/}
              {/*    isObject*/}
              {/*    icon="/images/conv_logo.png"*/}
              {/*/>*/}
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Box sx={{ bgcolor: "#FFFFFF", borderRadius: "15px", boxSizing: "border-box", padding: "10px", height: "100%" }}>
                <MapComponent
                  layerNumber={(() => {
                    return stages[selectedStageIndex]?.serial;
                  })()}
                  activeLayerNumber={stages[selectedStageIndex]?.project_layer}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <ButtonBase
                component="button"
                onClick={() => setShowQuarterlyModal(true)}
                focusRipple
                sx={{
                  width: "100%",
                  textAlign: "right",
                  borderRadius: 2,
                  p: 2,
                  bgcolor: "#FFFFFF",
                  cursor: "pointer",
                  display: "block",
                }}
                dir="rtl"
                ref={focusRef}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <InfoOutlined sx={{ fontSize: 24, color: "#0C3768", ml: 1 }} />

                    <Box>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0C3768" }}>להשוואה בין הדיווחים בשלב זה</Typography>

                      <Typography
                        sx={{
                          display: "block",
                          fontSize: 14,
                          fontWeight: 400,
                          color: "#0C3768",
                        }}
                      >
                        יש עיכוב של כ-{stages[selectedStageIndex].days_late} ימים.
                      </Typography>
                    </Box>
                  </Box>

                  <KeyboardArrowLeft sx={{ fontSize: 24, color: "#0C3768", ml: 1, opacity: "30%" }} />
                </Box>
              </ButtonBase>
            </Grid>
          </Grid>

          {/* כאן הקומפוננטה שנפתחת בלחיצה */}
          {stages?.[selectedStageIndex]?.serial && (
            <QuarterlyReportsModal
              open={showQuarterlyModal}
              onOpenChange={onCloseQuarterlyModal}
              projectId={id!}
              serialNumber={stages[selectedStageIndex]?.serial}
              type="stage"
            />
          )}
        </Grid>
        <Grid size={2}></Grid>
      </Grid>

      {stages[selectedStageIndex].has_children && (
        <div className="project-details-goto-sections">
          <Link
            style={{ textDecoration: "none", color: "inherit", cursor: "pointer", display: "block", marginBottom: 30 }}
            to={`/project/sections/${id}/${selectedStageIndex}`}
          >
            <Typography fontSize={22} fontWeight={700}>
              לצפייה במקטעים של {stages[selectedStageIndex].name}
            </Typography>
            <img src="/images/arrow_down.png" alt="up arrow" className="sticky-arrow" />
          </Link>
        </div>
      )}
    </Box>
  );
};

export default ProjectDetailsPage;
